import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import AddUserModal from 'components/AddUserModal/AddUserModal';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import DeactivationModal from 'components/DeactivationModals/DeactivationModal';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import Textarea from 'components/Textarea/Textarea';
import TextField from 'components/TextField/TextField';
import Tooltip from 'components/Tooltip/Tooltip';
import ValueChainHeader from 'components/ValueChainHeader/ValueChainHeader';
import { TASK_HEAD_VC_TABLE_CELLS } from 'constants/dataSets';
import {
  DESCRIPTION, NAME, STANDARDS, TASK_ASSIGNED_TO,
  VALUE_CHAIN_MAPS,
} from 'constants/formFields';
import {
  DEFAULT_MAX_LIMITED, DEFAULT_VC_COUNT,
  MATERIALITY_ASSESSMENT_STATUS,
} from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';

import AssignedUsersTable from './components/AssignedUsersTable';
import InitialVCmodal from './components/InitialVCmodal';
import VCTable from './components/VCTable';

import type IMaterialityAssessmentInitial from './interfaces/IMaterialityAssessmentInitial';
import type { IStandard } from './interfaces/IStandard';

import styles from './sass/MaterialityAssessmentInitial.module.scss';

function MaterialityAssessmentInitialView({
  control, watch,
  assignedUsers, onOpenUserModal,
  onOpenJobPositionModal, onDeleteUser, handleStandardChange,
  goBack, onHandleNext, isNextButtonDisabled, activeUserModal,
  onCloseUserModal, setValue, isCommentModalVisible,
  onRejectJobPositionModal, onSubmitJobPositionModal,
  valueChainMaps, onOpenVCModal, onCloseVCModal, activeVCModal,
  fieldText, standardOptions, onCancelVCModal, handleDraftSave,
  draftStatus, isAddVCButtonDisabled, isViewOnly = false,
}: IMaterialityAssessmentInitial) {
  const { t } = useTranslation();

  return (
    <div className={styles.root}>
      <span className={styles.header}>{t('materialityAssessment.titleInitiate')}</span>
      <span className={styles.hint}>
        *
        {t('materialityAssessment.hintInitiate')}
      </span>
      <div className={styles.rootContainer}>
        <div className={styles.wrapperTextFields}>
          <Controller
            control={control}
            name={NAME}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.name')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholderName')}
                disabled={isViewOnly}
              />
            )}
          />
          <Controller
            control={control}
            name={DESCRIPTION}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <Textarea
                name={name}
                value={value}
                label={t('materialityAssessment.description')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholderDescription')}
                charactersLimit={DEFAULT_MAX_LIMITED}
                className={styles.textarea}
                disabled={isViewOnly}
              />
            )}
          />
        </div>

        <div className={styles.vcWrapper}>
          <div className={styles.headerWrapper}>
            <ValueChainHeader
              title={t('materialityAssessment.valueChainMaps')}
              description={t('materialityAssessment.valueChainMapDescription')}
              selectedCount={watch(VALUE_CHAIN_MAPS)?.length || 0}
              totalCount={DEFAULT_VC_COUNT}
            />
            {valueChainMaps && valueChainMaps?.length > 0 ? (
              <Button
                title={t('materialityAssessment.addValueChain')}
                onClick={onOpenVCModal}
                className={styles.buttonHeader}
                startIcon={<PlusIcon />}
                disabled={isAddVCButtonDisabled || isViewOnly}
              />
            ) : null}
          </div>
          <div className={styles.contentWrapper}>
            {valueChainMaps.length > 0 ? (
              <div className={styles.tableWrapper}>
                <VCTable
                  items={valueChainMaps}
                  headCells={TASK_HEAD_VC_TABLE_CELLS}
                />
              </div>
            ) : (
              <EmptyTaskManagement
                title={t('materialityAssessment.emptyValueChainDescription')}
                buttonTitle={t('materialityAssessment.addValueChain')}
                className={styles.emptyUserDescription}
                buttonOnClick={onOpenVCModal}
                disabledButton={isAddVCButtonDisabled || isViewOnly}
              />
            )}
          </div>
        </div>

        <div className={styles.vcWrapper}>
          <div className={styles.headerWrapper}>
            <ValueChainHeader
              title={t('materialityAssessment.taskAssignedTo')}
              description={t('materialityAssessment.taskAssignedToDescription')}
              hideCounter
            />
            {assignedUsers && assignedUsers?.length > 0 ? (
              <Button
                title={t('materialityAssessment.btnAddUser')}
                onClick={onOpenUserModal}
                className={styles.buttonHeader}
                startIcon={<PlusIcon />}
                disabled={isViewOnly}
              />
            ) : null}
          </div>
          <div className={styles.contentWrapper}>
            {assignedUsers && assignedUsers?.length > 0 ? (
              <AssignedUsersTable
                usersInfo={assignedUsers}
                onOpenJobPositionModal={onOpenJobPositionModal}
                onDeleteUser={onDeleteUser}
                btnTitle={t('materialityAssessment.addJobPosition')}
                isViewOnly={isViewOnly}
              />
            ) : (
              <EmptyTaskManagement
                title={t('materialityAssessment.emptyUserDescription')}
                buttonTitle={t('materialityAssessment.btnAddUser')}
                className={styles.emptyUserDescription}
                buttonOnClick={onOpenUserModal}
                disabledButton={isViewOnly}
              />
            )}
          </div>
        </div>

        <div className={styles.vcWrapper}>
          <ValueChainHeader
            title={t('materialityAssessment.reportingStandards')}
            description={t('materialityAssessment.reportingStandardsDescription')}
            hideCounter
          />
          <div className={styles.checkboxesWrapper}>
            {standardOptions.map((standard) => {
              const isChecked = ((watch(STANDARDS) || []) as IStandard[]).some((s) => s.value === standard.value);
              return (
                <Checkbox
                  key={standard.value}
                  name={`standard-${standard.value}`}
                  value={standard.value}
                  label={standard.label}
                  checked={isChecked}
                  onChange={handleStandardChange(standard)}
                  className={`${styles.checkboxStandard} ${isChecked ? styles.checked : ''}`}
                  disabled={isViewOnly}
                />
              );
            })}
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={goBack}
          />
          <div className={styles.rightButtons}>
            {(!draftStatus || draftStatus === MATERIALITY_ASSESSMENT_STATUS.DRAFT) && (
              <Button
                title={t('common.draft')}
                onClick={handleDraftSave}
                variant={BUTTON_VARIANT.cancel}
                className={styles.draftButton}
                disabled={isViewOnly}
              />
            )}

            {isNextButtonDisabled ? (
              <Tooltip
                hint={t('materialityAssessment.hintInitiate')}
                className={styles.tooltip}
              >
                <Button
                  title={t('common.next')}
                  onClick={onHandleNext}
                  className={styles.button}
                  disabled={isNextButtonDisabled
                    || draftStatus === MATERIALITY_ASSESSMENT_STATUS.DRAFT}
                />
              </Tooltip>
            ) : (
              <Button
                title={t('common.next')}
                onClick={onHandleNext}
                className={styles.button}
                disabled={isNextButtonDisabled}
              />
            )}
          </div>

        </div>
        {activeUserModal ? (
          <AddUserModal
            open={activeUserModal}
            onClose={onCloseUserModal}
            setValueForm={setValue}
            currentSelectedUsers={watch(TASK_ASSIGNED_TO)?.map((user) => ({
              value: user?.value || '',
              label: user?.label || '',
              position: user?.position || '',
              role: user?.role || '',
              email: user?.email || '',
            }))}
          />
        ) : null}
        {activeVCModal ? (
          <InitialVCmodal
            open={activeVCModal}
            onClose={onCloseVCModal}
            onCancel={onCancelVCModal}
            setValueForm={setValue}
            currentSelectedVC={valueChainMaps}
            accessEdit={draftStatus ? draftStatus === MATERIALITY_ASSESSMENT_STATUS.DRAFT : undefined}
          />
        ) : null}
        <DeactivationModal
          open={isCommentModalVisible}
          title={t('materialityAssessment.addJobPosition')}
          placeholder={t('materialityAssessment.typeJobPositionHere')}
          onReject={onRejectJobPositionModal}
          onSubmit={onSubmitJobPositionModal}
          fieldText={fieldText}
          hideLabel
          isLoading={false}
          offValidation
          textFieldType
        />
      </div>
    </div>
  );
}

export default MaterialityAssessmentInitialView;
